import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import FirebaseCalendarComponent from './FirebaseCalendarComponent';
import { doc, getDoc } from 'firebase/firestore';
import NewEventModal, { EventOccurrenceContext } from './NewEventModal';
import { CalendarEvent, Job } from '../../job-summary/Interfaces';
import { db } from '../../../../firebase';
import { CalendarProvider, useCalendar } from './CalendarContext';
import { RepeatingInvoiceProvider } from '../../invoices/contexts/RepeatingInvoiceContext';
import { UnifiedCalendarItem, CalendarView, CalendarFilterState, toLocalDateString } from '../../../types/calendar';
import PaymentScheduleModal from '../../recurring/PaymentScheduleModal';
import { XeroInvoice } from '../../../types/xero';
import { RepeatingInvoice } from '../../../types/accounting';
import RepeatingInvoiceModal from './RepeatingInvoiceModal';
import InvoiceQuickViewModal from './InvoiceQuickViewModal';
import JobWorkspaceModal from '../../job-summary/JobWorkspaceModal';
import { useAppData } from '../../../providers/AppDataProvider';

/**
 * The pass-through seam between a host and the grid.
 *
 * All optional: `/calendar` (CalendarPage) drives view + anchor day from the URL
 * and passes all four, while CalendarModal passes none and the grid keeps its
 * own internal state. Both hosts render THIS component — there is no second
 * calendar implementation.
 */
interface CalendarCardProps {
  supportOnly?: boolean;
  customerIds?: string[];
  initialFilters?: Partial<CalendarFilterState>;
  /** Refresh the shared projections after an owning screen saves its source records. */
  refreshKey?: string;
  onSupportSelect?: (customerId: string, month: string) => void;
  view?: CalendarView;
  onViewChange?: (view: CalendarView) => void;
  /** Anchor day, local 'YYYY-MM-DD' (or a Date). */
  date?: string | Date;
  onDateChange?: (date: Date) => void;
}

/**
 * Any calendar start/occurrence value -> the local 'YYYY-MM-DD' it renders on.
 * All-day items store a bare day, timed items a full ISO instant, and
 * `new Date('2026-08-03')` is UTC midnight — which on Adelaide's +9:30/+10:30 is
 * mid-morning the SAME day, so the local day is what we want either way.
 */
function toDayString(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? value : toLocalDateString(parsed);
}

// "Doc is gone" and "the read failed" are very different things for the user —
// one is a stale tile to shrug off, the other is worth retrying — so the result
// is discriminated instead of collapsing both to null (which read as silence).
type EventFetchResult =
  | { status: 'found'; event: CalendarEvent }
  | { status: 'missing' }
  | { status: 'failed' };

async function fetchEventFromFirestore(eventId: string): Promise<EventFetchResult> {
  try {
    const eventDoc = doc(db, 'events', eventId);
    const eventSnapshot = await getDoc(eventDoc);
    if (!eventSnapshot.exists()) return { status: 'missing' };
    const data = eventSnapshot.data() as CalendarEvent;
    return { status: 'found', event: { ...data, id: eventId } as CalendarEvent };
  } catch (error) {
    console.error('Failed to fetch event:', error);
    return { status: 'failed' };
  }
}

// Inner component that uses the calendar context
const FirebaseCalendarInner: React.FC<CalendarCardProps> = ({ view, onViewChange, date, onDateChange, supportOnly, onSupportSelect }) => {
  const navigate = useNavigate();
  const {
    calendarItems,
    eventsLoading,
    filters,
    setFilters,
    setViewRange,
  } = useCalendar();

  // Jobs come from the same source TodayTimeline reads; re-resolving the clicked
  // job against it means the workspace never opens on a stale snapshot (the
  // pattern AttentionQueue uses).
  const { jobs, invoices } = useAppData();
  const [scheduleId, setScheduleId] = useState<string | null>(null);

  const [showEventModal, setShowEventModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showRepeatingModal, setShowRepeatingModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  // Which occurrence of a recurring series was opened (null for one-off events).
  // Held in state so its identity is stable — NewEventModal depends on it in an effect.
  const [selectedOccurrence, setSelectedOccurrence] = useState<EventOccurrenceContext | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  // The day the user asked to create an event on — NewEventModal prefills from it.
  const [createDate, setCreateDate] = useState<Date | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<XeroInvoice | null>(null);
  const [selectedInvoiceViewType, setSelectedInvoiceViewType] = useState<'invoice_due' | 'invoice_created' | 'bill_due'>('invoice_due');
  const [selectedRepeating, setSelectedRepeating] = useState<RepeatingInvoice | null>(null);

  const isLoading = eventsLoading;
  // Only the very first load may replace the calendar with a spinner. Once there
  // is anything to show, a later load must stay non-destructive (see the overlay
  // below): swapping the subtree out unmounts the calendar and silently throws
  // away the month the user had navigated to and any open day panel.
  const isFirstLoad = isLoading && calendarItems.length === 0;

  const handleCalendarItemClick = useCallback(async (item: UnifiedCalendarItem) => {
    if (item.commitment?.kind === 'support' && item.customerId && item.commitment.month) {
      if (onSupportSelect) onSupportSelect(item.customerId, item.commitment.month);
      else navigate(`/monthly-support?month=${encodeURIComponent(item.commitment.month)}&id=${encodeURIComponent(item.customerId)}`);
      return;
    }
    if (item.commitment?.kind === 'stripe' && item.commitment.subscriptionId) {
      setScheduleId(item.commitment.subscriptionId);
      return;
    }
    switch (item.type) {
      case 'event':
        if (item.eventData) {
          // An occurrence's own id is `event-<baseId>-<date>`, so the Firestore
          // document is `baseEventId`. Reading via the occurrence id would 404.
          const realId = item.baseEventId || item.eventData.id;
          const result = await fetchEventFromFirestore(realId);
          if (result.status === 'found') {
            const isSeries = !!(result.event.recurrence && result.event.recurrence.length > 0);
            // The base document is handed over UNMODIFIED — the clicked day
            // travels separately. Overwriting its start/end with the occurrence's
            // is what used to make "edit the series" silently move the series to
            // whichever day happened to be open.
            setSelectedEvent({
              ...result.event,
              id: realId,
              client: result.event.client || [],
            });
            setSelectedOccurrence(
              isSeries
                ? {
                    // `occurrenceDate` is only populated once the calendar
                    // materialises per-occurrence items; fall back to the day the
                    // chip itself renders on so this works either way.
                    date: toDayString(item.occurrenceDate || item.start),
                    start: item.start,
                    end: item.end,
                  }
                : null
            );
            setShowEventModal(true);
          } else if (result.status === 'missing') {
            toast.info('That event no longer exists — it may have been deleted.');
          } else {
            toast.error('Could not load that event. Check your connection and try again.');
          }
        }
        break;
      case 'job_due':
        if (item.jobData) {
          const jobId = item.jobData.id;
          setSelectedJob(jobs.find(j => j.id === jobId) || item.jobData);
        }
        break;
      case 'invoice_due':
      case 'invoice_created':
      case 'bill_due':
        if (item.invoiceData) {
          setSelectedInvoice(item.invoiceData);
          // Bills keep their own type — a supplier bill is money OUT and must not
          // be dressed up as an incoming invoice.
          setSelectedInvoiceViewType(item.type);
          setShowInvoiceModal(true);
        }
        break;
      case 'recurring_invoice':
      case 'recurring_expense':
        if (item.repeatingInvoiceData) {
          setSelectedRepeating(item.repeatingInvoiceData);
          setShowRepeatingModal(true);
        }
        break;
    }
  }, [jobs, navigate, onSupportSelect]);

  // selectedEvent MUST be null for a create — a blank-but-truthy object makes the
  // modal call itself "Edit Event". The requested day travels separately.
  const handleNewEvent = useCallback((date?: Date) => {
    setCreateDate(date ?? null);
    setSelectedEvent(null);
    setSelectedOccurrence(null);
    setShowEventModal(true);
  }, []);

  const closeEventModal = useCallback(() => {
    setShowEventModal(false);
    setSelectedEvent(null);
    setSelectedOccurrence(null);
    setCreateDate(null);
  }, []);

  const closeInvoiceModal = useCallback(() => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  }, []);

  const closeRepeatingModal = useCallback(() => {
    setShowRepeatingModal(false);
    setSelectedRepeating(null);
  }, []);

  const handleDatesSet = useCallback((start: Date, end: Date) => {
    setViewRange(start, end);
  }, [setViewRange]);

  const handleFilterChange = useCallback((newFilters: Partial<typeof filters>) => {
    setFilters(newFilters);
  }, [setFilters]);

  if (isFirstLoad) {
    return (
      <div className="calendar-loading">
        <div className="calendar-loading__content">
          <div className="calendar-loading__spinner"></div>
          <p className="calendar-loading__text">Loading Calendar</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Calendar with integrated toolbar and filters.
          The container (a modal body / page shell) owns the height — the calendar
          must not size itself off the viewport or a min-height floor, or its last
          week gets clipped on a phone. */}
      <div style={{ height: '100%', minHeight: 0, position: 'relative' }}>
        <FirebaseCalendarComponent
          calendarItems={calendarItems}
          onItemClick={handleCalendarItemClick}
          onDatesSet={handleDatesSet}
          onNewEvent={supportOnly ? undefined : handleNewEvent}
          supportOnly={supportOnly}
          filters={filters}
          onFilterChange={handleFilterChange}
          view={view}
          onViewChange={onViewChange}
          date={date}
          onDateChange={onDateChange}
        />

        {/* Subsequent loads are an overlay, so the calendar stays mounted and keeps
            the user's month and open day panel. */}
        {isLoading && (
          <div
            className="calendar-loading"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(var(--bs-body-bg-rgb, 255, 255, 255), 0.6)',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          >
            <div className="calendar-loading__content">
              <div className="calendar-loading__spinner"></div>
              <p className="calendar-loading__text">Loading Calendar</p>
            </div>
          </div>
        )}
      </div>

      {/* Event Modal — events stream in live from AppDataProvider's onSnapshot,
          so there is nothing to refresh after a save or delete. */}
      <NewEventModal
        isOpen={showEventModal}
        onClose={closeEventModal}
        event={selectedEvent}
        occurrence={selectedOccurrence}
        defaultDate={createDate}
      />

      {/* Job Workspace — clicking a job due-date chip opens the same workspace
          every other surface uses (AttentionQueue, EventLog, ClientDetailPanel…). */}
      <JobWorkspaceModal
        show={!!selectedJob}
        job={selectedJob}
        onHide={() => setSelectedJob(null)}
      />

      {/* Invoice Quick View Modal */}
      <InvoiceQuickViewModal
        isOpen={showInvoiceModal}
        onClose={closeInvoiceModal}
        invoice={selectedInvoice}
        viewType={selectedInvoiceViewType}
      />

      {/* Repeating Invoice Modal */}
      {scheduleId && <PaymentScheduleModal subscriptionId={scheduleId} onClose={() => setScheduleId(null)} onOpenInvoice={id => {
        const invoice = invoices.find(i => i.InvoiceID === id);
        if (!invoice) { toast.info('This invoice is not currently loaded. Open it from Invoices.'); return; }
        setSelectedInvoice(invoice); setSelectedInvoiceViewType('invoice_created'); setShowInvoiceModal(true);
      }} />}
      <RepeatingInvoiceModal
        isOpen={showRepeatingModal}
        onClose={closeRepeatingModal}
        repeatingInvoice={selectedRepeating}
      />
    </>
  );
};

// Main component wrapped with providers (RepeatingInvoiceProvider required for calendar recurring data)
const FirebaseCalendar: React.FC<CalendarCardProps> = (props) => {
  return (
    <RepeatingInvoiceProvider>
      <CalendarProvider customerIds={props.customerIds} refreshKey={props.refreshKey} initialFilters={{ ...(props.supportOnly ? {
        showEvents: false, showJobs: false, showInvoices: false, showRecurringExpenses: false,
        showRecurringInvoices: false, recurringShowBills: false, showStripePayments: false,
        showSupport: true, showCompletedSupport: true,
      } : {}), ...props.initialFilters }}>
        <FirebaseCalendarInner {...props} />
      </CalendarProvider>
    </RepeatingInvoiceProvider>
  );
};

export default FirebaseCalendar;
